<template>
  <v-container
    fluid
    class="intelligence-team-builder pa-4"
    style="position: relative; min-height: 400px"
  >
    <v-row>
      <v-col cols="12">
        <h1 class="text-h5 font-weight-bold mb-1 theme-heading">
          Team Builder
        </h1>
        <p class="text-subtitle-2 grey--text text--darken-1 mb-4">
          Describe your objective and we'll recommend a complementary team with
          diverse skills.
        </p>
      </v-col>
    </v-row>

    <v-stepper
      v-model="workflowStep"
      alt-labels
      flat
      class="elevation-0 mb-4"
      style="background: transparent"
    >
      <v-stepper-header style="box-shadow: none">
        <v-stepper-step :complete="workflowStep > 1" step="1" color="#007AFF">
          Context
        </v-stepper-step>
        <v-divider />
        <v-stepper-step :complete="workflowStep > 2" step="2" color="#007AFF">
          Roles
        </v-stepper-step>
        <v-divider />
        <v-stepper-step :complete="workflowStep > 3" step="3" color="#007AFF">
          Draft Pick
        </v-stepper-step>
        <v-divider />
        <v-stepper-step :complete="workflowStep > 4" step="4" color="#10B981">
          Report
        </v-stepper-step>
      </v-stepper-header>
    </v-stepper>

    <v-alert
      v-if="hasError && !isDecomposing && !isLoading"
      type="error"
      class="mb-4"
    >
      <div class="d-flex align-center justify-space-between">
        <span>{{ errorMessage }}</span>
        <v-btn
          small
          outlined
          color="error"
          class="ml-3"
          @click="retryLastAction"
        >
          <v-icon small left>mdi-refresh</v-icon>
          Retry
        </v-btn>
      </div>
    </v-alert>

    <v-overlay :value="isDecomposing" absolute opacity="0.85" z-index="10">
      <v-card
        class="pa-6 text-center"
        style="max-width: 480px; border-radius: 16px"
        dark
      >
        <v-progress-circular
          indeterminate
          size="56"
          width="4"
          color="#007AFF"
          class="mb-4"
        />
        <h3 class="text-h6 font-weight-bold mb-2">Analyzing Your Objective</h3>
        <p class="text-body-2 mb-3" style="opacity: 0.8">
          {{ decomposingStatusText }}
        </p>
        <v-progress-linear
          indeterminate
          color="#007AFF"
          rounded
          height="4"
          class="mb-2"
        />
        <p class="text-caption" style="opacity: 0.5">
          This typically takes 5–15 seconds
        </p>
      </v-card>
    </v-overlay>

    <v-overlay
      :value="isLoading && workflowStep === 2"
      absolute
      opacity="0.85"
      z-index="10"
    >
      <v-card
        class="pa-6 text-center"
        style="max-width: 520px; border-radius: 16px"
        dark
      >
        <v-progress-circular
          indeterminate
          size="56"
          width="4"
          color="#007AFF"
          class="mb-4"
        />
        <h3 class="text-h6 font-weight-bold mb-2">Building Your Team</h3>
        <p class="text-body-2 mb-3" style="opacity: 0.8">
          Analyzing {{ totalProfileCount }} profiles against {{ roles.length }}
          {{ roles.length === 1 ? "role" : "roles" }}...
        </p>
        <div class="text-left mb-3" style="width: 100%">
          <div
            v-for="(role, rIdx) in roles"
            :key="'progress-' + rIdx"
            class="mb-2"
          >
            <div class="d-flex align-center justify-space-between mb-1">
              <span class="text-caption" style="opacity: 0.7">{{
                role.title || "Role " + (rIdx + 1)
              }}</span>
              <v-icon
                x-small
                :color="buildProgressForRole(rIdx) ? '#10B981' : 'grey'"
                class="ml-2"
              >
                {{
                  buildProgressForRole(rIdx)
                    ? "mdi-check-circle"
                    : "mdi-timer-sand"
                }}
              </v-icon>
            </div>
            <v-progress-linear
              :indeterminate="!buildProgressForRole(rIdx)"
              :value="buildProgressForRole(rIdx) ? 100 : 0"
              :color="buildProgressForRole(rIdx) ? '#10B981' : '#007AFF'"
              rounded
              height="4"
            />
          </div>
        </div>
        <p class="text-caption" style="opacity: 0.5">
          Estimated time: {{ estimatedBuildTime }}
        </p>
      </v-card>
    </v-overlay>

    <!-- STEP 1: Context -->
    <div v-if="workflowStep === 1">
      <v-row>
        <v-col cols="12" md="8">
          <v-card outlined class="pa-4 mb-4">
            <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
              What are you building?
            </h3>
            <v-textarea
              :value="objective"
              @input="setObjective($event)"
              outlined
              dense
              rows="2"
              auto-grow
              color="#007AFF"
              placeholder="e.g., Build an AI product team to launch a customer-facing chatbot"
              hide-details
              class="mb-3"
            />

            <h3 class="text-subtitle-2 font-weight-bold mb-2 theme-heading">
              Project context
              <span class="text-caption grey--text font-weight-regular ml-1"
                >(optional — paste a project brief, SOW, or requirements)</span
              >
            </h3>
            <v-textarea
              :value="projectContext"
              @input="setProjectContext($event)"
              outlined
              dense
              rows="4"
              auto-grow
              color="#007AFF"
              placeholder="Paste your project requirements, SOW, or brief here. The more context you provide, the better the role recommendations will be."
              hide-details
              class="mb-4"
            />

            <div class="d-flex align-center" style="gap: 12px">
              <v-btn
                color="#007AFF"
                dark
                :loading="isDecomposing"
                :disabled="!objective || !objective.trim()"
                @click="analyzeObjective"
              >
                <v-icon left>mdi-brain</v-icon>
                Analyze &amp; Suggest Roles
              </v-btn>
              <v-btn text color="#007AFF" @click="skipToManual">
                I know what roles I need
              </v-btn>
            </div>
          </v-card>

          <div
            class="text-caption font-weight-bold grey--text text--darken-1 mb-2"
          >
            Example objectives:
          </div>
          <div class="d-flex flex-wrap" style="gap: 8px">
            <v-chip
              v-for="(example, idx) in exampleObjectives"
              :key="idx"
              outlined
              small
              @click="useExample(example)"
              class="example-chip"
            >
              {{ example }}
            </v-chip>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- CLARIFICATION PANEL -->
    <div v-if="showClarification && workflowStep === 1">
      <v-row>
        <v-col cols="12" md="8">
          <v-card outlined class="pa-4 mb-4 clarification-card">
            <div class="d-flex align-center mb-3">
              <v-icon color="#F59E0B" class="mr-2"
                >mdi-lightbulb-on-outline</v-icon
              >
              <h3
                class="text-subtitle-1 font-weight-bold theme-heading"
                style="margin: 0"
              >
                We can build a better team with more detail
              </h3>
            </div>
            <p class="text-body-2 mb-3" style="opacity: 0.85">
              {{
                objectiveFeedback && objectiveFeedback.message
                  ? objectiveFeedback.message
                  : "Adding more detail could improve team composition."
              }}
            </p>
            <div class="d-flex flex-wrap mb-3" style="gap: 8px">
              <v-chip
                v-for="(suggestion, sIdx) in clarificationSuggestions"
                :key="'cs-' + sIdx"
                :outlined="!activeClarifications[suggestionKey(suggestion)]"
                :color="
                  activeClarifications[suggestionKey(suggestion)]
                    ? '#007AFF'
                    : undefined
                "
                :dark="!!activeClarifications[suggestionKey(suggestion)]"
                small
                @click="toggleClarificationChip(suggestionKey(suggestion))"
                class="clarification-chip"
              >
                <v-icon x-small left>{{
                  activeClarifications[suggestionKey(suggestion)]
                    ? "mdi-check"
                    : suggestion.icon || "mdi-plus"
                }}</v-icon>
                {{ suggestionKey(suggestion) }}
              </v-chip>
            </div>
            <div
              v-for="(suggestion, sIdx) in clarificationSuggestions"
              :key="'ci-' + sIdx"
              class="mb-3"
            >
              <div v-if="activeClarifications[suggestionKey(suggestion)]">
                <div
                  class="text-caption font-weight-bold mb-1"
                  style="color: #007AFF"
                >
                  {{ suggestionKey(suggestion) }}
                </div>
                <v-text-field
                  :value="clarificationAnswers[suggestionKey(suggestion)] || ''"
                  @input="
                    setClarificationAnswer({
                      dimension: suggestionKey(suggestion),
                      value: $event,
                    })
                  "
                  outlined
                  dense
                  hide-details
                  color="#007AFF"
                  :placeholder="
                    suggestion.description ||
                      'e.g., Specify ' +
                        suggestionKey(suggestion).toLowerCase() +
                        ' details...'
                  "
                  class="mb-1"
                />
              </div>
            </div>
            <div class="d-flex align-center mt-3" style="gap: 12px">
              <v-btn
                color="#007AFF"
                dark
                :loading="isDecomposing"
                :disabled="!hasAnyClarification"
                @click="reanalyzeWithContext"
              >
                <v-icon left small>mdi-refresh</v-icon>
                Re-analyze with context
              </v-btn>
              <v-btn text color="#007AFF" @click="skipClarificationStep">
                Continue without changes
              </v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- STEP 2: Roles -->
    <div v-if="workflowStep === 2">
      <v-row>
        <v-col cols="12" md="8">
          <v-card
            v-if="decomposedStrategy"
            outlined
            class="pa-4 mb-4 strategy-card"
          >
            <div class="d-flex align-center mb-2">
              <v-icon small color="#3b82f6" class="mr-2"
                >mdi-lightbulb-on</v-icon
              >
              <span
                class="text-subtitle-2 font-weight-bold"
                style="color: #3b82f6"
                >Team Strategy</span
              >
            </div>
            <p class="text-body-2 mb-0 strategy-text" style="line-height: 1.6">
              {{ decomposedStrategy }}
            </p>
          </v-card>

          <div v-if="decompositionUnknowns.length > 0" class="mb-4">
            <div
              v-for="(unknown, uIdx) in decompositionUnknowns"
              :key="'u-' + uIdx"
              class="unknown-card pa-3 mb-3"
            >
              <div class="text-body-2 font-weight-medium mb-2 theme-heading">
                {{ unknown.question }}
              </div>
              <div class="d-flex flex-wrap" style="gap: 8px">
                <v-chip
                  v-for="(option, oIdx) in unknown.optionChips"
                  :key="oIdx"
                  small
                  :color="
                    unknown.answered && unknown.selectedOption === option
                      ? '#007AFF'
                      : 'white'
                  "
                  :text-color="
                    unknown.answered && unknown.selectedOption === option
                      ? 'white'
                      : '#007AFF'
                  "
                  :outlined="
                    !(unknown.answered && unknown.selectedOption === option)
                  "
                  class="unknown-chip"
                  @click="selectUnknownOption(uIdx, option)"
                >
                  {{ option }}
                </v-chip>
              </div>
            </div>
          </div>

          <div class="d-flex align-center justify-space-between mb-3">
            <h3 class="text-subtitle-1 font-weight-bold theme-heading">
              Roles ({{ roles.length }})
              <span
                v-if="decomposedHeadcount > 0"
                class="text-caption grey--text ml-2"
              >
                {{ decomposedHeadcount }} total headcount
              </span>
            </h3>
            <v-btn small outlined color="#007AFF" @click="addRole">
              <v-icon small left>mdi-plus</v-icon>
              Add Role
            </v-btn>
          </div>

          <div
            v-for="(role, idx) in roles"
            :key="idx"
            class="role-card pa-3 mb-3"
          >
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="text-caption font-weight-bold theme-heading">
                Role {{ idx + 1 }}
                <span
                  v-if="decomposedRoles[idx] && decomposedRoles[idx].reasoning"
                  class="text-caption grey--text font-weight-regular ml-1"
                >
                  — {{ decomposedRoles[idx].reasoning }}
                </span>
              </span>
              <v-btn
                v-if="roles.length > 1"
                icon
                x-small
                color="#EF4444"
                @click="removeRole(idx)"
              >
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </div>

            <v-row dense>
              <v-col cols="12" sm="5">
                <v-text-field
                  :value="role.title"
                  label="Role Title"
                  outlined
                  dense
                  hide-details
                  color="#007AFF"
                  @input="updateRoleField(idx, 'title', $event)"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  :value="role.count"
                  label="Headcount"
                  outlined
                  dense
                  hide-details
                  color="#007AFF"
                  type="number"
                  min="1"
                  max="20"
                  @input="updateRoleField(idx, 'count', parseInt($event) || 1)"
                />
              </v-col>
              <v-col cols="6" sm="4">
                <v-select
                  :value="role.seniorityPreference"
                  :items="seniorityOptions"
                  label="Seniority"
                  outlined
                  dense
                  hide-details
                  color="#007AFF"
                  clearable
                  @input="updateRoleField(idx, 'seniorityPreference', $event)"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  :value="role.requirements"
                  label="Requirements & key skills"
                  outlined
                  dense
                  rows="2"
                  hide-details
                  color="#007AFF"
                  @input="updateRoleField(idx, 'requirements', $event)"
                />
              </v-col>
            </v-row>
          </div>

          <div class="d-flex align-center" style="gap: 12px">
            <v-btn
              color="#007AFF"
              dark
              :loading="isLoading"
              :disabled="!hasValidRoles"
              @click="handleBuildTeam"
            >
              <v-icon left>mdi-account-group</v-icon>
              Build Team
            </v-btn>
            <v-btn text color="grey darken-1" @click="goToStep(1)">
              <v-icon small left>mdi-arrow-left</v-icon>
              Back
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- STEP 3: Draft Pick -->
    <div v-if="workflowStep === 3 && draftActive">
      <v-row>
        <v-col cols="12" md="4">
          <v-card outlined class="pa-3 mb-4">
            <h3 class="text-subtitle-2 font-weight-bold mb-3 theme-heading">
              Draft Progress
            </h3>
            <div v-for="(role, rIdx) in draftRoles" :key="rIdx" class="mb-2">
              <div class="d-flex align-center justify-space-between mb-1">
                <span
                  class="text-caption"
                  :style="{
                    fontWeight: rIdx === draftRoleIndex ? 'bold' : 'normal',
                    color: rIdx === draftRoleIndex ? '#007AFF' : '#666',
                  }"
                >
                  {{ role.roleTitle || "Role " + (rIdx + 1) }}
                </span>
                <span
                  class="text-caption font-weight-bold"
                  :style="{ color: isRoleComplete(rIdx) ? '#10B981' : '#999' }"
                >
                  {{ filledCountForRole(rIdx) }}/{{ role.targetCount }}
                </span>
              </div>
              <v-progress-linear
                :value="(filledCountForRole(rIdx) / role.targetCount) * 100"
                :color="
                  isRoleComplete(rIdx)
                    ? '#10B981'
                    : rIdx === draftRoleIndex
                    ? '#007AFF'
                    : '#E0E0E0'
                "
                height="6"
                rounded
              />
            </div>
            <v-divider class="my-3" />
            <div class="d-flex align-center justify-space-between">
              <span class="text-caption grey--text">Total selected</span>
              <v-chip small color="#007AFF" text-color="white">{{
                allDraftPicks.length
              }}</v-chip>
            </div>

            <div class="mt-3 d-flex" style="gap: 8px">
              <v-btn
                small
                outlined
                color="grey"
                :disabled="draftHistory.length === 0"
                @click="undoDraft"
              >
                <v-icon small left>mdi-undo</v-icon>
                Undo
              </v-btn>
              <v-btn
                small
                color="#10B981"
                dark
                @click="finalizeDraft"
                :disabled="allDraftPicks.length === 0"
              >
                Finalize Team
              </v-btn>
            </div>
          </v-card>

          <v-card v-if="allDraftPicks.length > 0" outlined class="pa-3">
            <h4
              class="text-caption font-weight-bold grey--text text--darken-1 mb-2"
            >
              Selected so far
            </h4>
            <div
              v-for="(pick, pIdx) in allDraftPicks"
              :key="pIdx"
              class="d-flex align-center justify-space-between mb-1"
            >
              <div>
                <span class="text-caption font-weight-bold theme-heading">{{
                  pick.name
                }}</span>
                <span class="text-caption grey--text ml-1">{{
                  pick.assignedRole
                }}</span>
              </div>
              <v-chip
                x-small
                :color="pick.overallScore >= 70 ? '#E8F5E9' : '#E3F2FD'"
                :text-color="pick.overallScore >= 70 ? '#10B981' : '#007AFF'"
              >
                {{ pick.overallScore }}%
              </v-chip>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="8">
          <div v-if="currentDraftRole && !isTeamComplete">
            <h3 class="text-subtitle-1 font-weight-bold mb-1 theme-heading">
              {{ currentDraftRole.roleTitle || "Role " + (draftRoleIndex + 1) }}
            </h3>
            <p class="text-caption grey--text mb-3">
              Slot {{ filledCountForRole(draftRoleIndex) + 1 }} of
              {{ currentDraftRole.targetCount }} —
              {{ currentDraftRole.candidates.length }} candidates evaluated
            </p>

            <v-alert
              v-if="currentRoleBestScore < 25"
              type="warning"
              text
              dense
              class="mb-3"
              color="#F59E0B"
              style="border-left: 4px solid #F59E0B"
            >
              <div class="d-flex align-center">
                <v-icon small color="#F59E0B" class="mr-2">mdi-alert-circle-outline</v-icon>
                <span class="text-body-2">
                  <strong>Talent Gap Identified:</strong>
                  No internal candidate shows strong evidence for this role (best match: {{ currentRoleBestScore }}%).
                  Consider broadening search criteria, adjusting seniority expectations, or exploring external hiring for this position.
                </span>
              </div>
            </v-alert>

            <div v-if="currentCandidateForRole">
              <v-card outlined class="mb-3" style="border: 2px solid #007AFF">
                <v-card-title class="py-2 px-4">
                  <div class="flex-grow-1">
                    <div class="text-subtitle-1 font-weight-bold theme-heading">
                      {{ currentCandidateForRole.name }}
                    </div>
                    <div class="text-caption grey--text text--darken-1">
                      {{ currentCandidateForRole.currentTitle }}
                      <span v-if="currentCandidateForRole.currentCompany">
                        at {{ currentCandidateForRole.currentCompany }}</span
                      >
                    </div>
                  </div>
                  <v-chip
                    small
                    :color="scoreColor(currentCandidateForRole.overallScore)"
                    text-color="white"
                    class="font-weight-bold"
                  >
                    {{ currentCandidateForRole.overallScore }}%
                  </v-chip>
                </v-card-title>

                <v-divider />

                <v-card-text class="py-3 px-4">
                  <div class="d-flex align-center mb-2">
                    <v-chip
                      x-small
                      outlined
                      :color="
                        categoryColor(currentCandidateForRole.matchCategory)
                      "
                      class="mr-2"
                    >
                      {{ currentCandidateForRole.matchCategory || "Consider" }}
                    </v-chip>
                    <v-chip
                      x-small
                      outlined
                      color="primary"
                      class="mr-2"
                      v-if="currentCandidateForRole.seniorityLevel"
                    >
                      {{ currentCandidateForRole.seniorityLevel }}
                    </v-chip>
                  </div>

                  <div
                    class="mb-3"
                    v-if="currentCandidateForRole.categoryExplanation"
                  >
                    <div class="text-caption grey--text mb-1">
                      Why this match
                    </div>
                    <div class="text-body-2" style="color: #333">
                      {{ currentCandidateForRole.categoryExplanation }}
                    </div>
                  </div>

                  <div class="mb-3" v-if="candidateTopSkills.length > 0">
                    <div class="text-caption grey--text mb-1">Key Skills</div>
                    <v-chip
                      v-for="skill in candidateTopSkills"
                      :key="skill"
                      x-small
                      color="#E3F2FD"
                      text-color="#007AFF"
                      class="mr-1 mb-1"
                    >
                      {{ skill }}
                    </v-chip>
                  </div>

                  <div
                    v-if="
                      currentCandidateForRole.fitSignals &&
                        currentCandidateForRole.fitSignals.length > 0
                    "
                    class="mb-3"
                  >
                    <div class="text-caption grey--text mb-1">Fit Signals</div>
                    <div
                      v-for="(signal,
                      sIdx) in currentCandidateForRole.fitSignals.slice(0, 3)"
                      :key="sIdx"
                      class="d-flex align-center mb-1"
                    >
                      <v-icon x-small color="#10B981" class="mr-1"
                        >mdi-check-circle</v-icon
                      >
                      <span class="text-caption">{{ signal }}</span>
                    </div>
                  </div>

                  <v-expansion-panels flat accordion>
                    <v-expansion-panel>
                      <v-expansion-panel-header class="px-0 py-1">
                        <span
                          class="text-caption font-weight-bold"
                          style="color: #007AFF"
                        >
                          Reviewing Evidence
                        </span>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <div v-if="candidateBreakdown" class="mb-3">
                          <v-row dense class="mb-2">
                            <v-col cols="6">
                              <div class="score-metric pa-2">
                                <div class="d-flex align-center justify-center">
                                  <span class="text-caption grey--text">Coverage</span>
                                  <v-tooltip bottom max-width="320">
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon x-small class="ml-1" v-bind="attrs" v-on="on" color="grey">mdi-help-circle-outline</v-icon>
                                    </template>
                                    <span class="text-caption">{{ metricExplanation('coverage') }}</span>
                                  </v-tooltip>
                                </div>
                                <div class="text-body-2 font-weight-bold theme-heading">
                                  {{ Math.round(candidateBreakdown.coveragePct || 0) }}%
                                </div>
                              </div>
                            </v-col>
                            <v-col cols="6">
                              <div class="score-metric pa-2">
                                <div class="d-flex align-center justify-center">
                                  <span class="text-caption grey--text">Avg Strength</span>
                                  <v-tooltip bottom max-width="320">
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon x-small class="ml-1" v-bind="attrs" v-on="on" color="grey">mdi-help-circle-outline</v-icon>
                                    </template>
                                    <span class="text-caption">{{ metricExplanation('avgStrength') }}</span>
                                  </v-tooltip>
                                </div>
                                <div class="text-body-2 font-weight-bold theme-heading">
                                  {{
                                    Math.round(
                                      (candidateBreakdown.avgStrength || 0) > 1
                                        ? candidateBreakdown.avgStrength || 0
                                        : (candidateBreakdown.avgStrength || 0) * 100,
                                    )
                                  }}%
                                </div>
                              </div>
                            </v-col>
                            <v-col cols="6">
                              <div class="score-metric pa-2">
                                <div class="d-flex align-center justify-center">
                                  <span class="text-caption grey--text">Proven Bonus</span>
                                  <v-tooltip bottom max-width="320">
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon x-small class="ml-1" v-bind="attrs" v-on="on" color="grey">mdi-help-circle-outline</v-icon>
                                    </template>
                                    <span class="text-caption">{{ metricExplanation('provenBonus') }}</span>
                                  </v-tooltip>
                                </div>
                                <div class="text-body-2 font-weight-bold theme-heading">
                                  {{ ((candidateBreakdown.provenBonus || 1) * 100 - 100).toFixed(0) }}%
                                </div>
                              </div>
                            </v-col>
                            <v-col cols="6">
                              <div class="score-metric pa-2">
                                <div class="d-flex align-center justify-center">
                                  <span class="text-caption grey--text">Recency</span>
                                  <v-tooltip bottom max-width="320">
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon x-small class="ml-1" v-bind="attrs" v-on="on" color="grey">mdi-help-circle-outline</v-icon>
                                    </template>
                                    <span class="text-caption">{{ metricExplanation('recency') }}</span>
                                  </v-tooltip>
                                </div>
                                <div class="text-body-2 font-weight-bold theme-heading">
                                  {{ ((candidateBreakdown.recencyFactor || 1) * 100).toFixed(0) }}%
                                </div>
                              </div>
                            </v-col>
                          </v-row>

                          <div
                            v-if="candidateBreakdown.explanation"
                            class="text-caption mb-3"
                            style="color: #333"
                          >
                            {{ candidateBreakdown.explanation }}
                          </div>

                          <div v-if="candidateFacets.length > 0">
                            <div class="text-caption grey--text mb-1">
                              Facet Breakdown
                            </div>
                            <div
                              v-for="(facet, fIdx) in candidateFacets"
                              :key="fIdx"
                              class="mb-2"
                            >
                              <div
                                class="d-flex align-center justify-space-between mb-1"
                              >
                                <span
                                  class="text-caption"
                                  :style="{
                                    color: facet.matched ? '#1E3A5F' : '#999',
                                  }"
                                >
                                  {{ facet.facetName }}
                                  <v-icon
                                    x-small
                                    :color="
                                      facet.matched ? '#10B981' : '#E0E0E0'
                                    "
                                    class="ml-1"
                                  >
                                    {{
                                      facet.matched
                                        ? "mdi-check-circle"
                                        : "mdi-close-circle"
                                    }}
                                  </v-icon>
                                </span>
                                <span
                                  class="text-caption font-weight-bold"
                                  :style="{
                                    color: facet.matched ? '#10B981' : '#999',
                                  }"
                                >
                                  {{ Math.round((facet.strength || 0) * 100) }}%
                                </span>
                              </div>
                              <v-progress-linear
                                :value="(facet.strength || 0) * 100"
                                :color="facet.matched ? '#10B981' : '#E0E0E0'"
                                height="4"
                                rounded
                              />
                              <div
                                v-if="facet.evidenceSpan"
                                class="text-caption grey--text mt-1"
                                style="font-style: italic"
                              >
                                "{{
                                  facet.evidenceSpan.length > 120
                                    ? facet.evidenceSpan.substring(0, 120) +
                                      "..."
                                    : facet.evidenceSpan
                                }}"
                              </div>
                            </div>
                          </div>
                        </div>

                        <div v-if="candidateFacetMatches.length > 0">
                          <div class="text-caption grey--text mb-1">
                            Additional Facet Matches
                          </div>
                          <div
                            v-for="(fm, fmIdx) in candidateFacetMatches"
                            :key="'fm-' + fmIdx"
                            class="mb-2"
                          >
                            <div
                              class="d-flex align-center justify-space-between"
                            >
                              <span class="text-caption">{{
                                fm.facetName || fm.facet_name
                              }}</span>
                              <span
                                class="text-caption font-weight-bold"
                                :style="{ color: getScoreColor(fm.score) }"
                                >{{ Math.round(fm.score || 0) }}%</span
                              >
                            </div>
                            <div
                              v-if="fm.evidence"
                              class="text-caption grey--text"
                              style="font-style: italic"
                            >
                              {{
                                fm.evidence.length > 100
                                  ? fm.evidence.substring(0, 100) + "..."
                                  : fm.evidence
                              }}
                            </div>
                          </div>
                        </div>

                        <div
                          v-if="currentCandidateForRole.strongestSignal"
                          class="mt-2"
                        >
                          <div class="text-caption grey--text">
                            Strongest Signal
                          </div>
                          <div
                            class="text-caption font-weight-bold"
                            style="color: #10B981"
                          >
                            {{ currentCandidateForRole.strongestSignal }}
                          </div>
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-card-text>

                <v-card-actions class="px-4 py-3">
                  <v-btn color="#10B981" dark @click="acceptCurrentCandidate">
                    <v-icon left>mdi-check</v-icon>
                    Accept for Team
                  </v-btn>
                  <v-btn
                    outlined
                    color="grey darken-1"
                    @click="skipCurrentCandidate"
                  >
                    <v-icon left>mdi-skip-next</v-icon>
                    Skip
                  </v-btn>
                  <v-spacer />
                  <v-btn
                    small
                    text
                    color="#007AFF"
                    @click="viewProfile(currentCandidateForRole)"
                  >
                    <v-icon small left>mdi-account-eye</v-icon>
                    Full Profile
                  </v-btn>
                </v-card-actions>
              </v-card>
            </div>

            <div v-else class="text-center py-6">
              <v-icon size="48" color="#F59E0B" class="mb-2"
                >mdi-account-question</v-icon
              >
              <h4 class="text-subtitle-2 font-weight-bold mb-2 theme-heading">
                No more candidates for this role
              </h4>
              <p class="text-caption grey--text mb-3">
                All available candidates have been reviewed. You can undo
                previous skips or move to the next role.
              </p>
              <v-alert
                v-if="currentRoleBestScore < 25"
                type="info"
                text
                dense
                class="mb-3 text-left"
                color="#F59E0B"
              >
                <strong>External Hiring Recommended:</strong>
                No internal candidate demonstrated strong evidence for this role.
                This may be a good opportunity to recruit externally.
              </v-alert>
              <div class="d-flex justify-center" style="gap: 8px">
                <v-btn
                  small
                  outlined
                  color="grey"
                  :disabled="draftHistory.length === 0"
                  @click="undoDraft"
                >
                  <v-icon small left>mdi-undo</v-icon>
                  Undo
                </v-btn>
                <v-btn
                  v-if="draftRoleIndex < draftRoles.length - 1"
                  small
                  color="#007AFF"
                  dark
                  @click="moveToNextRole"
                >
                  Next Role
                </v-btn>
                <v-btn
                  v-else
                  small
                  color="#10B981"
                  dark
                  @click="finalizeDraft"
                  :disabled="allDraftPicks.length === 0"
                >
                  Finalize Team
                </v-btn>
              </div>
            </div>
          </div>

          <div v-else-if="isTeamComplete" class="text-center py-8">
            <v-icon size="64" color="#10B981" class="mb-3"
              >mdi-check-circle</v-icon
            >
            <h3 class="text-subtitle-1 font-weight-bold mb-2 theme-heading">
              All roles filled!
            </h3>
            <p class="text-body-2 grey--text text--darken-1 mb-4">
              You've selected {{ allDraftPicks.length }} team members across
              {{ draftRoles.length }} roles.
            </p>
            <v-btn color="#10B981" dark @click="finalizeDraft">
              <v-icon left>mdi-file-document</v-icon>
              View Team Report
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- STEP 4: Team Report -->
    <div v-if="workflowStep === 4 && draftFinalized">
      <v-row>
        <v-col cols="12" md="10">
          <v-card outlined class="pa-5 mb-4">
            <div class="d-flex align-center justify-space-between mb-4">
              <div>
                <h3 class="text-h6 font-weight-bold theme-heading">
                  Team Report
                </h3>
                <p class="text-caption grey--text mb-0">
                  {{ new Date().toLocaleDateString() }}
                </p>
              </div>
              <div class="d-flex" style="gap: 8px">
                <v-btn
                  small
                  outlined
                  color="#007AFF"
                  @click="exportReport('clipboard')"
                >
                  <v-icon small left>mdi-content-copy</v-icon>
                  Copy
                </v-btn>
                <v-btn
                  small
                  outlined
                  color="#007AFF"
                  @click="exportReport('json')"
                >
                  <v-icon small left>mdi-download</v-icon>
                  JSON
                </v-btn>
                <v-btn
                  small
                  outlined
                  color="#007AFF"
                  @click="exportReport('text')"
                >
                  <v-icon small left>mdi-file-document</v-icon>
                  Text
                </v-btn>
              </div>
            </div>

            <div v-if="objective" class="mb-3">
              <div class="text-caption font-weight-bold grey--text">
                Objective
              </div>
              <div class="text-body-2">{{ objective }}</div>
            </div>

            <div v-if="reportStrategy" class="strategy-card pa-3 mb-4">
              <div class="text-caption font-weight-bold" style="color: #3b82f6">
                Strategy
              </div>
              <div class="text-body-2">{{ reportStrategy }}</div>
            </div>

            <h4 class="text-subtitle-2 font-weight-bold mb-3 theme-heading">
              Selected Team ({{ teamReportData.totalMembers }} members)
            </h4>

            <div
              v-for="(member, mIdx) in teamReportData.roles"
              :key="mIdx"
              class="report-member-card pa-3 mb-3"
            >
              <div class="d-flex align-center justify-space-between mb-2">
                <div>
                  <span
                    class="text-caption font-weight-bold"
                    style="color: #007AFF"
                    >{{ member.roleTitle }}</span
                  >
                </div>
                <v-chip
                  small
                  :color="scoreColor(member.score)"
                  text-color="white"
                >
                  {{ member.score }}%
                </v-chip>
              </div>
              <div class="text-subtitle-2 font-weight-bold theme-heading">
                {{ member.name }}
              </div>
              <div class="text-caption grey--text">
                {{ member.seniorityLevel }}
                <span v-if="member.matchCategory">
                  · {{ member.matchCategory }}</span
                >
              </div>
              <div
                v-if="member.currentTitle"
                class="text-caption mb-2"
                style="color: #555"
              >
                {{ member.currentTitle }}
              </div>
              <div
                v-if="member.keySkills && member.keySkills.length > 0"
                class="mb-2"
              >
                <v-chip
                  v-for="sk in member.keySkills.slice(0, 5)"
                  :key="sk"
                  x-small
                  color="#E3F2FD"
                  text-color="#007AFF"
                  class="mr-1 mb-1"
                >
                  {{ sk }}
                </v-chip>
              </div>
              <div
                v-if="member.evidenceSummary"
                class="text-caption mb-1"
                style="color: #666"
              >
                {{ member.evidenceSummary }}
              </div>
              <div
                v-if="
                  member.starAchievements &&
                    member.starAchievements.length > 0
                "
                class="mt-1"
              >
                <v-icon x-small color="amber" class="mr-1">mdi-star</v-icon>
                <span class="text-caption" style="color: #F59E0B">
                  {{ member.starAchievements.length }} STAR
                  achievement{{
                    member.starAchievements.length > 1 ? "s" : ""
                  }}
                  on record
                </span>
              </div>
            </div>

            <div
              v-if="
                teamReportData.unfilledRoles &&
                  teamReportData.unfilledRoles.length > 0
              "
              class="mt-4"
            >
              <h4
                class="text-subtitle-2 font-weight-bold mb-2"
                style="color: #F59E0B"
              >
                Unfilled Roles ({{ teamReportData.unfilledRoles.length }})
              </h4>
              <div
                v-for="(uf, ufIdx) in teamReportData.unfilledRoles"
                :key="'uf-' + ufIdx"
                class="unfilled-card pa-3 mb-2"
              >
                <span class="text-body-2 font-weight-bold">{{
                  uf.roleTitle
                }}</span>
                <span class="text-caption grey--text ml-2"
                  >Target: {{ uf.targetCount }} ·
                  {{ uf.candidatesAvailable }} candidates evaluated</span
                >
                <div class="text-caption mt-1" style="color: #F59E0B">
                  <v-icon x-small color="#F59E0B" class="mr-1">mdi-alert-circle-outline</v-icon>
                  Consider external hiring for this position
                </div>
              </div>
            </div>

            <div
              v-if="
                teamReportData.skillsCoverage &&
                  teamReportData.skillsCoverage.length > 0
              "
              class="mt-4"
            >
              <h4
                class="text-subtitle-2 font-weight-bold mb-2"
                style="color: #10B981"
              >
                Team Skills Coverage
              </h4>
              <div class="d-flex flex-wrap" style="gap: 6px">
                <v-chip
                  v-for="sc in teamReportData.skillsCoverage.slice(0, 15)"
                  :key="sc.skill"
                  small
                  :color="sc.count > 1 ? '#E8F5E9' : '#E3F2FD'"
                  :text-color="sc.count > 1 ? '#10B981' : '#007AFF'"
                >
                  {{ sc.skill }}
                  <span v-if="sc.count > 1" class="ml-1 font-weight-bold"
                    >×{{ sc.count }}</span
                  >
                </v-chip>
              </div>
            </div>
          </v-card>

          <div class="d-flex" style="gap: 8px">
            <v-btn text color="grey darken-1" @click="goToStep(3)">
              <v-icon small left>mdi-arrow-left</v-icon>
              Back to Draft
            </v-btn>
            <v-btn text color="#007AFF" @click="startOver">
              <v-icon small left>mdi-restart</v-icon>
              Start Over
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </div>

    <v-snackbar v-model="snackbar" :timeout="2000" color="#10B981">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";

const EXAMPLE_OBJECTIVES = [
  "Build an AI product team from scratch",
  "Launch a new mobile app for enterprise clients",
  "Lead a digital transformation initiative",
  "Turn around an underperforming sales region",
];

const SENIORITY_OPTIONS = [
  { text: "Any", value: "" },
  { text: "Junior", value: "Junior" },
  { text: "Mid-Level", value: "Mid" },
  { text: "Senior", value: "Senior" },
  { text: "Lead", value: "Lead" },
  { text: "Director", value: "Director" },
  { text: "VP", value: "VP" },
  { text: "Executive", value: "Executive" },
];

export default {
  name: "IntelligenceTeamBuilder",
  props: {
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      exampleObjectives: EXAMPLE_OBJECTIVES,
      seniorityOptions: SENIORITY_OPTIONS,
      snackbar: false,
      snackbarText: "",
      lastAction: null,
      decomposingTextIndex: 0,
      decomposingInterval: null,
      buildStartTime: null,
    };
  },
  watch: {
    isDecomposing(val) {
      if (val) {
        this.decomposingTextIndex = 0;
        this.decomposingInterval = setInterval(() => {
          this.decomposingTextIndex = (this.decomposingTextIndex + 1) % 4;
        }, 3000);
      } else {
        clearInterval(this.decomposingInterval);
        this.decomposingInterval = null;
      }
    },
    isLoading(val) {
      if (val) {
        this.buildStartTime = Date.now();
      }
    },
  },
  beforeDestroy() {
    clearInterval(this.decomposingInterval);
  },
  computed: {
    ...mapState("intelligenceTeamBuilder", [
      "roles",
      "roleResults",
      "totalCandidates",
      "isLoading",
      "hasError",
      "errorMessage",
      "objective",
      "projectContext",
      "teamSummary",
      "teamStrategy",
      "requiredRoles",
      "skillsCoverage",
      "unfilledRoles",
      "showAlternatives",
      "limitReached",
      "workflowStep",
      "decomposedRoles",
      "decompositionUnknowns",
      "decomposedStrategy",
      "decomposedHeadcount",
      "isDecomposing",
      "objectiveFeedback",
      "clarificationAnswers",
      "showClarification",
      "draftActive",
      "draftRoleIndex",
      "draftPicks",
      "skippedByRole",
      "draftHistory",
      "draftFinalized",
    ]),
    ...mapGetters("intelligenceTeamBuilder", [
      "hasResults",
      "totalRoles",
      "draftRoles",
      "currentDraftRole",
      "currentCandidateForRole",
      "filledCountForRole",
      "isRoleComplete",
      "isTeamComplete",
      "allDraftPicks",
      "teamReport",
    ]),
    hasValidRoles() {
      return this.roles.some((r) => r.title && r.title.trim());
    },
    clarificationSuggestions() {
      if (!this.objectiveFeedback || !this.objectiveFeedback.suggestions)
        return [];
      return this.objectiveFeedback.suggestions.map(function(s) {
        if (typeof s === "object" && s !== null) return s;
        return { label: String(s), description: "", icon: "mdi-plus" };
      });
    },
    activeClarifications() {
      var active = {};
      var answers = this.clarificationAnswers || {};
      for (var i = 0; i < this.clarificationSuggestions.length; i++) {
        var key =
          this.clarificationSuggestions[i].label ||
          String(this.clarificationSuggestions[i]);
        if (answers[key] !== undefined) active[key] = true;
      }
      return active;
    },
    hasAnyClarification() {
      var answers = this.clarificationAnswers || {};
      return Object.values(answers).some(function(v) {
        return v && v.trim();
      });
    },
    decomposingStatusText() {
      var texts = [
        "Breaking down your objective into optimal roles...",
        "Identifying skill requirements and seniority levels...",
        "Crafting team strategy and role definitions...",
        "Finalizing role recommendations...",
      ];
      return texts[this.decomposingTextIndex] || texts[0];
    },
    totalProfileCount() {
      return this.totalCandidates || 0;
    },
    estimatedBuildTime() {
      var count = this.roles.length;
      if (count <= 2) return "10–20 seconds";
      if (count <= 4) return "15–30 seconds";
      return "20–45 seconds";
    },
    currentRoleBestScore() {
      if (!this.currentDraftRole || !this.currentDraftRole.candidates) return 0;
      var scores = this.currentDraftRole.candidates.map(function(c) { return c.overallScore || 0; });
      return scores.length > 0 ? Math.max.apply(null, scores) : 0;
    },
    candidateTopSkills() {
      if (!this.currentCandidateForRole) return [];
      return (this.currentCandidateForRole.keySkills || []).slice(0, 6);
    },
    candidateBreakdown() {
      if (!this.currentCandidateForRole) return null;
      return this.currentCandidateForRole.scoreBreakdown || null;
    },
    candidateFacets() {
      if (!this.candidateBreakdown) return [];
      return this.candidateBreakdown.facetBreakdowns || [];
    },
    candidateFacetMatches() {
      if (!this.currentCandidateForRole) return [];
      return this.currentCandidateForRole.facetMatches || [];
    },
    teamReportData() {
      return (
        this.teamReport || {
          roles: [],
          unfilledRoles: [],
          skillsCoverage: [],
          totalMembers: 0,
        }
      );
    },
    reportStrategy() {
      return this.teamStrategy || this.decomposedStrategy || "";
    },
  },
  methods: {
    metricExplanation(metric) {
      var fallbacks = {
        coverage: "Share of requested facets that have at least some matching evidence in the profile, weighted by facet importance.",
        avgStrength: "Average evidence strength across matched facets. Strength is higher for STAR achievements with quantified outcomes.",
        provenBonus: "Bonus when claims are backed by STAR or small-win evidence; penalty when too many claims are skill-only without demonstration.",
        recency: "How recent the supporting evidence is. Drops as evidence ages beyond the last two years.",
      };
      var explanations = this.candidateBreakdown && this.candidateBreakdown.metricExplanations;
      if (explanations && explanations[metric]) return explanations[metric];
      return fallbacks[metric] || "";
    },
    ...mapActions("intelligenceTeamBuilder", [
      "decomposeObjective",
      "buildTeam",
      "buildFromObjective",
      "exportTeamReport",
      "reanalyzeWithClarification",
      "skipClarification",
    ]),
    ...mapMutations("intelligenceTeamBuilder", [
      "addRole",
      "removeRole",
      "updateRole",
      "setObjective",
      "setProjectContext",
      "setMode",
      "setWorkflowStep",
      "applyDecomposedRoles",
      "answerUnknown",
      "acceptCandidate",
      "skipCandidate",
      "undoDraft",
      "advanceDraftRole",
      "finalizeDraft",
      "resetDraft",
      "startDraft",
      "toggleAlternatives",
      "setClarificationAnswer",
    ]),
    updateRoleField(index, field, value) {
      this.updateRole({ index, field, value });
    },
    goToStep(step) {
      this.setWorkflowStep(step);
    },
    analyzeObjective() {
      this.lastAction = "decompose";
      this.decomposeObjective();
    },
    handleBuildTeam() {
      this.lastAction = "build";
      this.buildTeam();
    },
    skipToManual() {
      this.setWorkflowStep(2);
    },
    suggestionKey(suggestion) {
      if (typeof suggestion === "object" && suggestion !== null)
        return suggestion.label || String(suggestion);
      return String(suggestion);
    },
    toggleClarificationChip(key) {
      if (this.activeClarifications[key]) {
        this.setClarificationAnswer({ dimension: key, value: undefined });
      } else {
        this.setClarificationAnswer({ dimension: key, value: "" });
      }
    },
    reanalyzeWithContext() {
      this.lastAction = "decompose";
      this.reanalyzeWithClarification();
    },
    skipClarificationStep() {
      this.skipClarification();
    },
    useExample(example) {
      this.setObjective(example);
    },
    selectUnknownOption(uIdx, option) {
      this.answerUnknown({ index: uIdx, option });
    },
    acceptCurrentCandidate() {
      if (!this.currentCandidateForRole) return;
      this.acceptCandidate({
        roleIndex: this.draftRoleIndex,
        candidate: { ...this.currentCandidateForRole },
      });
      var target = this.currentDraftRole
        ? this.currentDraftRole.targetCount
        : 1;
      if (this.filledCountForRole(this.draftRoleIndex) >= target) {
        this.autoAdvanceRole();
      }
    },
    skipCurrentCandidate() {
      if (!this.currentCandidateForRole) return;
      this.skipCandidate({
        roleIndex: this.draftRoleIndex,
        profileId: this.currentCandidateForRole.profileId,
      });
    },
    autoAdvanceRole() {
      if (this.draftRoleIndex < this.draftRoles.length - 1) {
        this.advanceDraftRole();
      }
    },
    moveToNextRole() {
      this.advanceDraftRole();
    },
    viewProfile(candidate) {
      this.$router.push({
        name: "IntelligenceProfile",
        params: { profileId: candidate.profileId },
        query: { from: "teambuilder" },
      });
    },
    async exportReport(format) {
      var result = await this.exportTeamReport(format);
      if (result === "copied") {
        this.snackbarText = "Team report copied to clipboard";
        this.snackbar = true;
      } else if (result === "downloaded") {
        this.snackbarText = "Report downloaded";
        this.snackbar = true;
      }
    },
    startOver() {
      this.$store.commit("intelligenceTeamBuilder/logout");
    },
    scoreColor(score) {
      if (score >= 70) return "#10B981";
      if (score >= 50) return "#F59E0B";
      return "#9E9E9E";
    },
    categoryColor(cat) {
      var colors = {
        "Best Match": "#10B981",
        "Strong Match": "#007AFF",
        "Match": "#007AFF",
        "Potential Match": "#F59E0B",
        "Potential Fit": "#F59E0B",
        "Consider": "#9E9E9E",
        "Weak": "#EF4444",
      };
      return colors[cat] || "#007AFF";
    },
    getScoreColor(value) {
      if (value >= 70) return "#10B981";
      if (value >= 50) return "#F59E0B";
      return "#EF4444";
    },
    buildProgressForRole(rIdx) {
      if (!this.roleResults || !this.roleResults[rIdx]) return false;
      return (
        this.roleResults[rIdx].candidates &&
        this.roleResults[rIdx].candidates.length > 0
      );
    },
    retryLastAction() {
      if (this.lastAction === "decompose") {
        this.analyzeObjective();
      } else {
        this.lastAction = "build";
        this.buildTeam();
      }
    },
  },
};
</script>

<style scoped>
.intelligence-team-builder {
  max-width: 1400px;
}
.role-card {
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}
.example-chip {
  cursor: pointer;
}
.example-chip:hover {
  background: #e3f2fd !important;
}
.clarification-card {
  background: linear-gradient(
    135deg,
    rgba(245, 158, 11, 0.08),
    rgba(251, 191, 36, 0.05)
  );
  border: 1px solid rgba(245, 158, 11, 0.25);
  border-radius: 8px;
}
.clarification-chip {
  cursor: pointer;
  transition: all 0.2s ease;
}
.clarification-chip:hover {
  transform: translateY(-1px);
}
.strategy-card {
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.08),
    rgba(139, 92, 246, 0.05)
  );
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
}
.unknown-card {
  background: #fff8e1;
  border: 1px solid #f59e0b;
  border-radius: 8px;
}
.unknown-chip {
  cursor: pointer;
  transition: transform 0.15s ease;
}
.unknown-chip:hover {
  transform: translateY(-1px);
}
.score-metric {
  background: #f5f7fa;
  border-radius: 6px;
  text-align: center;
}
.report-member-card {
  background: #f5f7fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
.unfilled-card {
  background: rgba(234, 179, 8, 0.1);
  border: 1px solid rgba(234, 179, 8, 0.3);
  border-radius: 8px;
}
</style>

<style>
.theme--dark .role-card {
  background: #2a2a3e;
  border-color: #444;
}
.theme--dark .example-chip:hover {
  background: rgba(66, 165, 245, 0.15) !important;
}
.theme--dark .clarification-card {
  background: linear-gradient(
    135deg,
    rgba(245, 158, 11, 0.15),
    rgba(251, 191, 36, 0.1)
  );
  border-color: rgba(245, 158, 11, 0.35);
}
.theme--dark .strategy-card {
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.15),
    rgba(139, 92, 246, 0.1)
  );
  border-color: rgba(59, 130, 246, 0.3);
}
.strategy-text {
  color: #333;
}
.theme--dark .strategy-text {
  color: #e0e0e0;
}
.theme--dark .unknown-card {
  background: rgba(245, 158, 11, 0.1);
  border-color: #f59e0b;
}
.theme--dark .score-metric {
  background: #2a2a3e;
}
.theme--dark .report-member-card {
  background: #2a2a3e;
  border-color: #444;
}
</style>
